import { Component, ErrorInfo, ReactNode } from 'react';
import styled from '@emotion/styled';
import { theme } from '../styles/theme';

/**
 * App-level error boundary.
 *
 * Note: the i18n `useTranslation` hook cannot be used inside a class
 * component (and this boundary may render when the LanguageProvider itself
 * has crashed), so the fallback strings are accepted via props with
 * bilingual TR/EN defaults.
 *
 * The CSS variables (--color-bg etc.) are defined by the <Global> styles
 * rendered inside the app tree. If the crash unmounted them, the hardcoded
 * fallback values below keep the fallback UI on-brand (dark theme).
 */

interface ErrorBoundaryProps {
  children: ReactNode;
  title?: string;
  message?: string;
  reloadLabel?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

const FallbackContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 24px;
  gap: 12px;
  text-align: center;
  background: var(--color-bg, #222222);
  color: var(--color-text, #ffffff);
  font-family: ${theme.typography.fontFamily.primary};
`;

const FallbackTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text, #ffffff);
`;

const FallbackMessage = styled.p`
  margin: 0;
  max-width: 320px;
  font-size: 14px;
  line-height: 1.5;
  color: var(--color-text-secondary, #c5c6ca);
`;

const ReloadButton = styled.button`
  margin-top: 12px;
  padding: 10px 24px;
  border: 1px solid var(--color-border, rgba(255, 255, 255, 0.2));
  border-radius: 12px;
  background: var(--color-primary, #834cc9);
  color: #ffffff;
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:active {
    transform: scale(0.97);
  }
`;

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(_error: Error): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('[ErrorBoundary] Uncaught render error:', error, errorInfo.componentStack);
  }

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    const {
      children,
      title = 'Bir şeyler ters gitti / Something went wrong',
      message = 'Beklenmedik bir hata oluştu. Lütfen sayfayı yenileyin. / An unexpected error occurred. Please reload the page.',
      reloadLabel = 'Yenile / Reload',
    } = this.props;

    if (this.state.hasError) {
      return (
        <FallbackContainer role="alert">
          <FallbackTitle>{title}</FallbackTitle>
          <FallbackMessage>{message}</FallbackMessage>
          <ReloadButton type="button" onClick={this.handleReload}>
            {reloadLabel}
          </ReloadButton>
        </FallbackContainer>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
